import { useState } from 'react'
import { useFormData, useFormStore, useFormActions, useGeneratedDocuments } from '@hooks/useFormStore'
import { signingApi } from '@services/api'
import { Button } from '@components/UI/Button'
import styles from './Signing.module.css'

export function OtpRequestSection() {
  const formData = useFormData()
  const { setStep, setSigningSessionId, setSigningSession } = useFormActions()
  const submissionId = useFormStore((state) => state.submissionId)
  const generatedDocuments = useGeneratedDocuments()
  
  const [contactType, setContactType] = useState<'email' | 'phone'>('phone')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Maschera contatto per privacy
  const maskContact = (type: 'email' | 'phone', value: string) => {
    if (type === 'email') {
      const [user, domain] = value.split('@')
      if (!domain || user.length <= 2) return value
      return `${user[0]}${'*'.repeat(user.length - 2)}${user[user.length - 1]}@${domain}`
    } else {
      if (value.length <= 4) return value
      return `${'*'.repeat(value.length - 4)}${value.slice(-4)}`
    }
  }

  const handleRequestOtp = async () => {
    if (!submissionId) {
      setError('Errore: submission non trovata')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // 1. Crea sessione di firma
      const documentName = generatedDocuments.length > 0 
        ? generatedDocuments[0].filename 
        : 'Proposta di Contratto'
      
      const pdfUrl = generatedDocuments.length > 0 
        ? generatedDocuments[0].url 
        : null

      const { session } = await signingApi.createSession({
        submission_id: submissionId,
        contact_type: contactType,
        document_name: documentName,
        pdf_url: pdfUrl || undefined
      })

      setSigningSessionId(session.id)
      setSigningSession(session)

      // 2. Richiedi invio OTP
      await signingApi.requestOtp(session.id)

      // 3. Passa allo step di verifica OTP
      setStep('otp_verify')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore durante l\'invio del codice'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.section}>
      {/* Progress Steps */}
      <div className={styles.progressSteps}>
        <div className={`${styles.progressStep} ${styles.progressStepActive}`}>
          <div className={styles.progressNumber}>1</div>
          <span className={styles.progressLabel}>Richiedi</span>
        </div>
        <div className={styles.progressLine} />
        <div className={styles.progressStep}>
          <div className={styles.progressNumber}>2</div>
          <span className={styles.progressLabel}>Verifica</span>
        </div>
        <div className={styles.progressLine} />
        <div className={styles.progressStep}>
          <div className={styles.progressNumber}>3</div>
          <span className={styles.progressLabel}>Firma</span>
        </div>
        <div className={styles.progressLine} />
        <div className={styles.progressStep}>
          <div className={styles.progressNumber}>4</div>
          <span className={styles.progressLabel}>Completa</span>
        </div>
      </div>

      {/* Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardIcon}>
            <i className="bi bi-shield-lock" />
          </div>
          <h2 className={styles.cardTitle}>Verifica la tua identità</h2>
          <p className={styles.cardDescription}>
            Per procedere con la firma del documento, è necessario verificare la tua identità.
          </p>
        </div>

        <div className={styles.cardContent}>
          {/* Document Info */}
          <div className={styles.documentInfo}>
            <div className={styles.documentInfoLabel}>Documento da firmare</div>
            <div className={styles.documentInfoValue}>
              {generatedDocuments.length > 0 
                ? generatedDocuments[0].filename 
                : 'Proposta di Contratto'}
            </div>
          </div>

          {/* Contact Method Selection */}
          <div className={styles.contactMethodSection}>
            <label className={styles.contactMethodLabel}>
              Ricevi il codice di verifica via:
            </label>
            <div className={styles.contactMethodGrid}>
              {/* Email Option */}
              <button
                type="button"
                className={`${styles.contactMethodCard} ${contactType === 'email' ? styles.contactMethodCardActive : ''}`}
                onClick={() => setContactType('email')}
                disabled={!formData.email}
              >
                <div className={styles.contactMethodIcon}>
                  <i className="bi bi-envelope" />
                </div>
                <div className={styles.contactMethodText}>
                  <span className={styles.contactMethodName}>Email</span>
                  <span className={styles.contactMethodValue}>
                    {formData.email ? maskContact('email', formData.email) : 'Non disponibile'}
                  </span>
                </div>
              </button>

              {/* Phone Option */}
              <button
                type="button"
                className={`${styles.contactMethodCard} ${contactType === 'phone' ? styles.contactMethodCardActive : ''}`}
                onClick={() => setContactType('phone')}
                disabled={!formData.telefono}
              >
                <div className={styles.contactMethodIcon}>
                  <i className="bi bi-phone" />
                </div>
                <div className={styles.contactMethodText}>
                  <span className={styles.contactMethodName}>SMS</span>
                  <span className={styles.contactMethodValue}>
                    {formData.telefono ? maskContact('phone', formData.telefono) : 'Non disponibile'}
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className={styles.testModeNotice} style={{ background: 'linear-gradient(145deg, #fee2e2 0%, #fef2f2 100%)', borderColor: '#fca5a5' }}>
              <p style={{ color: '#991b1b' }}>
                <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.5rem' }} />
                {error}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleRequestOtp}
            disabled={isLoading || (!formData.email && !formData.telefono)}
          >
            {isLoading ? (
              <>
                <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
                Invio in corso...
              </>
            ) : (
              'Richiedi codice OTP'
            )}
          </button>

          {/* Security Footer */}
          <div className={styles.securityFooter}>
            <i className="bi bi-lock" />
            <span>Processo di verifica sicuro</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={() => setStep('riepilogo')}
          disabled={isLoading}
        >
          Indietro
        </Button>
      </div>
    </div>
  )
}

